import { useInfiniteQuery } from '@tanstack/react-query';
import { api, DfeInventoryEntry, DfeInventoryListResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseInventoryOptions {
  search?: string;
  fungibility?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface UseInventoryReturn {
  items: DfeInventoryEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useInventory(options: UseInventoryOptions = {}): UseInventoryReturn {
  const search = options.search || undefined;
  const fungibility = options.fungibility || undefined;
  const sortBy = options.sortBy || 'name';
  const sortOrder = options.sortOrder || 'asc';

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: queryKeys.dfe.inventory({ search, fungibility, sortBy, sortOrder }),
    queryFn: ({ pageParam = 0 }): Promise<DfeInventoryListResult> =>
      api.getDfeInventory({
        search,
        fungibility,
        sortBy,
        sortOrder,
        limit: LIMIT,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const allItems = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    items: allItems,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load inventory' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
  };
}
